import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { DAY_OF_WEEK } from 'src/features/membershipType/types';
import { DAY_OF_WEEK_TRANSLATIONS_MAP } from 'src/features/membershipType/utils/constants';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { AllowedTimes } from 'src/features/membershipType/types';
import { useListMembershipTypeAllowedTimes } from 'src/features/membershipType/api/allowedTime/useListMembershipTypeAllowedTimes';
import CreateAllowedTimeFormModal from 'src/features/membershipType/components/forms/allowedTime/CreateAllowedTimeFormModal';
import CellAllowedTimeActions from 'src/features/membershipType/components/cell/CellAllowedTimeActions';
import { bgToEnDays } from 'src/features/membership/utils/constants';
import { allowedTimesMessages } from 'src/features/membershipType/utils/messages';
import { Translation } from 'src/types';
import { DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import { useParking } from 'src/providers/ParkingProvider';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/registry/hooks/usePagination';

const columnHelper = createColumnHelper<AllowedTimes>();
const translateSearchValue = (value: string) => {
    if (!value) return value;
    const lower = value.toLowerCase().trim();
    return bgToEnDays[lower] ?? value;
};

const translate = (translations: Translation[], locale: string) =>
    translations.find((translation) => translation.language === locale);

type Props = {
    membershipTypeId: string;
    inherited?: boolean;
};
const AllowedTimesTable = ({ inherited, membershipTypeId }: Props) => {
    const { formatMessage, locale } = useIntl();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination} = usePagination();

    const manageMembershipType = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIP_TYPES]);

    const { parkingId } = useParking();
    //search day_of_week must be sent in the original enum format
    const translatedSearchOptions = {
        search: translateSearchValue(querySearchOptions.search),
    };

    const { data: membershipTemplate } = useGetMembershipType({
        parkingId,
        membershipTypeId,
    });

    const templateName = React.useMemo(() => {
        if (!membershipTemplate?.translations) return '-';
        return translate(membershipTemplate?.translations, locale)?.name ?? '-';
    }, [membershipTemplate, locale]);

    const {
        data: allowedTimesData,
        isLoading,
        isError,
    } = useListMembershipTypeAllowedTimes({
        parkingId,
        membershipTypeId,
        ...translatedSearchOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        ...queryPaginationOptions,
        config: { enabled: !!membershipTypeId },
    });

    const weekDayOptions = React.useMemo(
        () =>
            Object.values(DAY_OF_WEEK).map((value) => ({
                label: formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[value]),
                value,
            })),
        [formatMessage],
    );

    const columns = [
        columnHelper.accessor('day_of_week', {
            enableHiding: true,
            header: DataTableColumnHeader,
            cell: (props) => {
                const dayOfWeek = props.row.original.day_of_week;
                return formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[dayOfWeek]) ?? '-';
            },
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(allowedTimesMessages.dayOfWeek),
                options: weekDayOptions,
            },
        }),
        columnHelper.accessor('from_time', {
            enableHiding: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(allowedTimesMessages.from),
            },
        }),
        columnHelper.accessor('to_time', {
            enableHiding: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(allowedTimesMessages.to),
            },
        }),
        ...(manageMembershipType
            ? [
                  columnHelper.display({
                      id: 'actions',
                      cell: (props) => (
                          <CellAllowedTimeActions allowedTimesData={allowedTimesData?.results} {...props} />
                      ),
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<AllowedTimes, any>
            pagination={pagination}
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={allowedTimesData}
            search={search}
            filters={filters}
            enableSearchParams={false}
            enableLocalFilters={false}
            semantics={{
                title: formatMessage(
                    inherited ? allowedTimesMessages.inheritedAllowedTime : allowedTimesMessages.allowedTime,
                    { entry: templateName },
                ),
                entryName: formatMessage(allowedTimesMessages.time).toLowerCase(),
                entryNames: formatMessage(allowedTimesMessages.times).toLowerCase(),
                initialColumnVisibility: { actions: !inherited },
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageMembershipType && !inherited && { newEntry: CreateAllowedTimeFormModal }),
            }}
        />
    );
};

export default AllowedTimesTable;
